import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Collections", path: "/products" },
  { name: "Manufacturing", path: "/manufacturing" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = ({ onOpenCatalogModal }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-cream/95 backdrop-blur-md shadow-soft border-b border-sand/20"
          : "bg-cream"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <img
              src={process.env.PUBLIC_URL + "/logo header.png"}
              alt="Reliable Exports"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-sand relative group ${
                  location.pathname === link.path
                    ? "text-forest"
                    : "text-muted-foreground"
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-sand transition-all duration-300 ${
                    location.pathname === link.path
                      ? "w-full"
                      : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              data-testid="download-catalog-btn"
              onClick={onOpenCatalogModal}
              className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-6 transition-all duration-300"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Catalog
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6 text-forest" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream w-[300px]">
              <div className="flex flex-col gap-6 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    className={`text-lg font-medium transition-colors hover:text-sand ${
                      location.pathname === link.path
                        ? "text-forest"
                        : "text-muted-foreground"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Button
                  data-testid="mobile-download-catalog-btn"
                  onClick={() => {
                    setIsOpen(false);
                    onOpenCatalogModal();
                  }}
                  className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-6 mt-4"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Catalog
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
